import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { placeableSegments } from '@src/editor/dubbing/DubPlacementService';
import { dubEndMs } from '@src/editor/dubbing/dubWordTimeline';
import {
  areCaptionsHidden,
  setCaptionsHidden,
} from '@src/services/karaoke/captionsVisibility';
import { GenericPresetTemplates } from '@src/types.ts/general';
import { normalizeLangKey } from '@src/utility/bcp47';

import {
  applyCaptionsLanguage,
  seedFallbackTemplateOnEditor,
} from './applyCaptionsLanguage';
import { correctKaraokeDataFonts } from './captionFontSubstitution';

import type { CaptionFallbackTemplate } from './applyCaptionsLanguage';
import type { ClipSpan } from '@src/editor/dubbing/DubPlacementService';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeData } from '@src/editor/types/karaoke';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { ResolvedDubManifest } from '@src/repositories/DubbingRepository';
import type { KaraokeConfig as KaraokeStyle } from '@src/services/karaoke/karaokeTypes';

/**
 * PLAT-11683 — the captions a Translation Review player shows, on the
 * review's OWN editor (the clip entry's renderer editor, the story entry's
 * transcript-scoped editor). Review-only: every command here runs with
 * `createUndoRedoEntry: false`, so nothing lands in an undo stack or a
 * host's autosave; the clip / story is written only through the host's
 * explicit paths.
 *
 * The cascade (PLAT-11683, narrowed by PLAT-11702): captions the video already
 * has → the showcase's own caption template → nothing.
 *
 *   0. PLAT-12251 — the video has saved captions in the reviewed language:
 *      load exactly those (`savedCaptionsByLanguage`), as the clip preview
 *      does, rather than producing them again.
 *   1. The editor already has a caption track (a clip with captions, or the
 *      story's saved style seeded through `savedStyle`): keep its style, only
 *      make sure the track is in the reviewed language.
 *   2. No track: seed the showcase's first custom caption template over the
 *      built-in default (`seedFallbackTemplateOnEditor`), then produce.
 *   3. No track and no showcase template: produce NOTHING (`'no-template'`).
 *      The built-in default is not the showcase's style, and a review that
 *      showed it would misrepresent what the showcase publishes (PLAT-11702).
 */
export type ReviewCaptionEditor = IVideoEditor &
  Pick<VideoEditor, 'commandProduceKaraoke' | 'commandRemoveTrack'>;

export interface EnsureReviewCaptionsOptions {
  /** The reviewed language (BCP 47) the track must be in. */
  language: string;
  /** The showcase's own caption template, when it has one (PLAT-10635). */
  fallbackTemplate?: CaptionFallbackTemplate;
  /**
   * Story entry: the style of the captions the story already has (its saved
   * default karaoke config), copied onto this editor before producing so the
   * review shows the story's captions as they were styled. `null` ⇒ the
   * story has none yet; the cascade continues with the showcase template.
   */
  savedStyle?: KaraokeConfig | null;
  /**
   * PLAT-11702 follow-up — the template the reviewer picked in the Captions
   * dropdown. Unlike `fallbackTemplate` (a seed used only when there is
   * nothing else to show), this is an explicit choice: it replaces the style
   * on a track that ALREADY exists and re-produces, so switching templates
   * mid-review redraws instead of silently keeping the first one. Still
   * review-only — like everything here, off the undo stack and never saved.
   */
  pickedTemplate?: CaptionFallbackTemplate | null;
  /**
   * PLAT-12251 — the video's own saved captions per language (canonical key),
   * as they were when the review opened. When the reviewed language has
   * translated captions here and no template is picked, the review shows them
   * as saved: loaded the way the clip preview loads them
   * (`switchCaptionLanguage`'s cache hit), so their line breaks, merges and
   * hidden words match it. A produce would rebuild them from scratch. Taken at
   * open because the editor's own cache is overwritten by whatever the review
   * shows (a template preview), and the way back must be to the saved ones.
   */
  savedCaptionsByLanguage?: Readonly<Record<string, KaraokeData>> | null;
  /**
   * The app's caption font catalog, which the saved captions' font correction
   * treats as substitutable (`correctKaraokeDataFonts`); passed in so this
   * module stays free of the app's utilities.
   */
  knownFontFamilies?: readonly string[];
}

export type EnsureReviewCaptionsResult =
  /** The editor now carries a caption track in the reviewed language. */
  | 'shown'
  /** The editor's karaoke config has not loaded yet; nothing produced. */
  | 'no-config'
  /**
   * No track, no saved style and no showcase template: nothing to show that
   * would be the showcase's own; nothing produced (PLAT-11702).
   */
  | 'no-template'
  /** The produce itself failed (no transcript, no clips). */
  | 'failed';

export function ensureReviewCaptions(
  editor: ReviewCaptionEditor,
  {
    language,
    fallbackTemplate,
    savedStyle,
    pickedTemplate,
    savedCaptionsByLanguage,
    knownFontFamilies,
  }: EnsureReviewCaptionsOptions,
): EnsureReviewCaptionsResult {
  if (!editor.getDefaultKaraokeConfig()) return 'no-config';
  const languageKey = normalizeLangKey(language);
  const saved = savedCaptionsByLanguage?.[languageKey];
  if (
    !pickedTemplate &&
    saved?.clips.some(clip => clip.translatedText != null)
  ) {
    // The clip preview's own font correction for the language (PLAT-11407).
    const fontSafe = correctKaraokeDataFonts(saved, languageKey, {
      knownFamilies: knownFontFamilies,
    });
    if (!editor.loadKaraokeData(fontSafe, { reconcileAgainstTranscript: true }))
      return 'failed';
    // PLAT-11689 — shown even where the clip's Cap toggle hid them.
    if (areCaptionsHidden(editor)) {
      setCaptionsHidden(editor, false, { createUndoRedoEntry: false });
    }
    // A translation edited since they were saved (in this review too, before a
    // template preview and back) re-texts the rows and keeps their structure.
    editor.commandSyncTranslatedCaptionText(false);
    return 'shown';
  }
  // PLAT-11689 — the review's Captions On shows captions even on a clip whose
  // Cap toggle hid them. This is the review's own editor, off the undo stack.
  if (areCaptionsHidden(editor)) {
    setCaptionsHidden(editor, false, { createUndoRedoEntry: false });
  }
  const config = editor.getDefaultKaraokeConfig();
  if (!config) return 'no-config';
  const applyStyle = (
    // A saved style may carry the clip's `hidden` — never re-hide here.
    { hidden: _hidden, ...style }: KaraokeStyle & { hidden?: boolean },
    templateId?: string,
  ) =>
    editor.commandUpdateKaraokeConfig(
      {
        ...style,
        id: config.id,
        language: config.language,
        ...(templateId ? { parent_template_id: templateId } : {}),
      },
      false,
    );
  const produce = () =>
    editor.commandProduceKaraoke({
      replaceTrack: true,
      createUndoRedoEntry: false,
    });
  // The style the Captions dropdown is asking for: the reviewer's own pick,
  // else the video's own captions. Its template id is what says whether the
  // track on screen is already in it.
  const requested = pickedTemplate
    ? { style: pickedTemplate.config, templateId: pickedTemplate.templateId }
    : savedStyle
      ? { style: savedStyle, templateId: savedStyle.parent_template_id }
      : null;
  // A track already up but in the WRONG style — the reviewer changed the
  // dropdown, either to a template or back to the video's own. Re-style and
  // re-produce so the picture follows. Both directions matter: without the
  // second, returning to the video's own style after previewing a template
  // would leave the template on screen.
  const restyle =
    requested &&
    editor.hasKaraokeTrack() &&
    config.parent_template_id !== requested.templateId
      ? requested
      : null;
  if (restyle) {
    applyStyle(restyle.style, restyle.templateId);
    if (!produce()) return 'failed';
  } else if (!editor.hasKaraokeTrack()) {
    // Nothing on screen yet: the reviewer's pick, else the video's own style,
    // else the showcase's seed, else nothing of the showcase's to show.
    if (pickedTemplate) {
      applyStyle(pickedTemplate.config, pickedTemplate.templateId);
    } else if (savedStyle) {
      // The story's own captions: its style, this editor's config identity.
      applyStyle(savedStyle);
    } else if (fallbackTemplate) {
      seedFallbackTemplateOnEditor(editor, fallbackTemplate, {
        targetLanguageKey: language,
      });
    } else {
      return 'no-template';
    }
    if (!produce()) return 'failed';
  }
  // Set the karaoke config to the reviewed language. When the editor's
  // config already matches (the common case — a clip loads its saved caption
  // language), this is a noop and does NOT re-produce the track; and the
  // editor's first produce may have run before the translation resolver was
  // registered, so the track could still be original-language. Force a
  // re-produce in that case so `ProduceKaraokeCommand` emits target-language
  // subtitles. Off the undo stack, like `applyCaptionsLanguage`.
  const result = applyCaptionsLanguage(editor, language, fallbackTemplate);
  // The re-style above already produced in this config; a noop language step
  // means the track on screen is the requested style in the reviewed language.
  if (restyle && result.kind === 'noop') return 'shown';
  if (result.kind !== 'applied') {
    // Non-applied path (usually noop): the language seed inside
    // `applyCaptionsLanguage` is skipped, so seed the template here before the
    // forced re-produce (PLAT-10635), unless the style came from the story.
    if (!savedStyle && fallbackTemplate) {
      seedFallbackTemplateOnEditor(editor, fallbackTemplate, {
        targetLanguageKey: language,
      });
    }
    const produced = editor.commandProduceKaraoke({
      replaceTrack: true,
      createUndoRedoEntry: false,
    });
    if (!produced) return 'failed';
  }
  return 'shown';
}

/**
 * Captions Off: remove the review editor's caption track, outside the undo
 * stack — a viewing choice, not an edit. Returns whether there was one.
 */
export function removeReviewCaptions(editor: ReviewCaptionEditor): boolean {
  if (!editor.hasKaraokeTrack()) return false;
  editor.commandRemoveTrack(String(KARAOKE_TRACK_NUMBER), false);
  return true;
}

/**
 * PLAT-11906 — where the story-entry review's dub is heard: one stretch from
 * the story's start to the dub's last line end, which is how the review lays
 * the voice over the story (`composeDubTrack`).
 */
export function storyDubStretch(resolved: ResolvedDubManifest): ClipSpan[] {
  const { live } = placeableSegments(resolved.manifest, resolved.renditionUrls);
  return [
    { sourceStartSec: 0, clipStartSec: 0, durationSec: dubEndMs(live) / 1000 },
  ];
}

/**
 * PLAT-11906 — the story-entry review plays the story's dub itself; hand the
 * caption editor that dub, so its captions follow the voice: the description
 * goes in the editor's holder, the dub is heard over `storyDubStretch`, and
 * Both shows the dub-timed captions as Dubbed does.
 */
export function followReviewDub(
  editor: ReviewCaptionEditor,
  language: string,
  resolved: ResolvedDubManifest,
): void {
  editor.dubSource.remember(language, resolved);
  editor.setExternalHeardDub({ language, spans: storyDubStretch(resolved) });
  editor.setBothFollowsDub(true);
}

/**
 * PLAT-11906 — undo `followReviewDub` when the dub stops or the review
 * closes, leaving the editor as the review found it.
 */
export function stopFollowingReviewDub(
  editor: ReviewCaptionEditor,
  language: string,
): void {
  editor.setExternalHeardDub(null);
  editor.setBothFollowsDub(false);
  editor.dubSource.forget(language);
}

/**
 * PLAT-11702 — the story's own caption style as `savedStyle` above, or `null`
 * to let the cascade seed the showcase's template instead. A config still on
 * the built-in default has no template behind it: it is Creator Studio's
 * first-produce look, not the showcase's, so the showcase's template wins.
 * But only when there IS one — with no showcase template at this aspect ratio
 * the choice is the story's real captions or none at all, and a story that
 * visibly has captions must not review as "No captions set up".
 */
export function resolveReviewSavedCaptionStyle(
  storyStyle: KaraokeConfig | null,
  hasShowcaseTemplate: boolean,
): KaraokeConfig | null {
  if (!storyStyle) return null;
  const isBuiltInDefault =
    !storyStyle.parent_template_id ||
    storyStyle.parent_template_id === GenericPresetTemplates.default;
  return isBuiltInDefault && hasShowcaseTemplate ? null : storyStyle;
}
